
import Vue from "vue";
import { api, PendingAction } from "@/services/api";

type ExpenseSnapshot = {
    id: number;
    date: string;
    amountCents: number;
    description: string;
    source: string;
    tags: string[];
    sensitive: boolean;
    budgetBucketId?: number;
    ruleBucket?: string;
    budgetBucketOverrideId?: number;
    bucketOverride?: string;
    splitId?: number;
    removalMode: string;
};

type ExpenseChange = {
    before?: ExpenseSnapshot;
    after?: ExpenseSnapshot;
};

type ExpensePreviewRow = { label: string; before: string; after: string };

type PendingExpenseSummary = {
    id?: number;
    Id?: number;
    date?: string;
    Date?: string;
    amountCents?: number;
    AmountCents?: number;
    description?: string;
    Description?: string;
    source?: string;
    Source?: string;
};

type PendingBucketSummary = {
    name?: string;
    Name?: string;
    slug?: string;
    Slug?: string;
};

type PendingExpenseUpdate = {
    id?: number;
    Id?: number;
    expense?: PendingExpenseSummary;
    Expense?: PendingExpenseSummary;
    tags?: string[];
    Tags?: string[];
    setBudgetBucketOverride?: boolean;
    SetBudgetBucketOverride?: boolean;
    budgetBucketOverrideId?: number | null;
    BudgetBucketOverrideId?: number | null;
    budgetBucketOverride?: PendingBucketSummary | null;
    BudgetBucketOverride?: PendingBucketSummary | null;
};

type PendingUpdatePayload = {
    updates?: PendingExpenseUpdate[];
};

type PendingRuleOrderItem = {
    id?: number;
    Id?: number;
    currentPriority?: number;
    CurrentPriority?: number;
    proposedPriority?: number;
    ProposedPriority?: number;
    description?: string;
    Description?: string;
    budgetBucket?: PendingBucketSummary | null;
    BudgetBucket?: PendingBucketSummary | null;
};

type PendingReorderPayload = {
    ruleOrder?: PendingRuleOrderItem[];
};

export default Vue.extend({
    data() {
        return {
            action: null as PendingAction | null,
            loadingAction: true,
            resultMessage: "",
            submitting: false
        };
    },
    async mounted() {
        await this.refresh();
    },
    computed: {
        token(): string {
            return this.$route.params.token;
        },
        canConfirm(): boolean {
            return !!this.action && !this.action.isExpired && !this.action.isTerminal && !this.submitting;
        },
        statusLabel(): string {
            if (!this.action) {
                return "Unavailable";
            }
            if (this.action.executedAt) {
                return "Executed";
            }
            if (this.action.cancelledAt) {
                return "Cancelled";
            }
            if (this.action.isExpired) {
                return "Expired";
            }
            if (this.action.lastErrorMessage) {
                return "Failed, can retry";
            }
            return "Awaiting confirmation";
        },
        payloadText(): string {
            if (!this.action) {
                return "";
            }
            return JSON.stringify(this.action.payload, null, 2);
        },
        expenseChanges(): ExpenseChange[] {
            const payload = this.action?.payload as { expenses?: ExpenseChange[] } | undefined;
            const expenses = payload?.expenses;
            return Array.isArray(expenses) ? expenses : [];
        },
        expenseUpdates(): PendingExpenseUpdate[] {
            const payload = this.action?.payload as PendingUpdatePayload | undefined;
            const updates = payload?.updates;
            return Array.isArray(updates) ? updates : [];
        },
        ruleOrder(): PendingRuleOrderItem[] {
            const payload = this.action?.payload as PendingReorderPayload | undefined;
            const rules = payload?.ruleOrder;
            return Array.isArray(rules) ? rules : [];
        },
        showRawPayload(): boolean {
            return this.expenseChanges.length === 0 && this.expenseUpdates.length === 0 && this.ruleOrder.length === 0;
        }
    },
    methods: {
        async refresh() {
            this.loadingAction = true;
            try {
                this.action = await api.getPendingAction(this.token);
            } finally {
                this.loadingAction = false;
            }
        },
        async confirm() {
            if (!this.canConfirm) return;
            this.submitting = true;
            try {
                const result = await api.confirmPendingAction(this.token);
                const ids = result.expenseIds?.length ? ` Expense IDs: ${result.expenseIds.join(", ")}.` : "";
                this.resultMessage = result.message + ids;
                await this.refresh();
            } finally {
                this.submitting = false;
            }
        },
        async cancel() {
            if (!this.canConfirm) return;
            this.submitting = true;
            try {
                const result = await api.cancelPendingAction(this.token);
                this.resultMessage = result.message;
                await this.refresh();
            } finally {
                this.submitting = false;
            }
        },
        previewRows(change: ExpenseChange): ExpensePreviewRow[] {
            const fields: Array<[string, (expense: ExpenseSnapshot) => string]> = [
                ["Description", expense => expense.description],
                ["Date", expense => expense.date.slice(0, 10)],
                ["Amount", expense => this.money(expense.amountCents)],
                ["Source", expense => expense.source || "None"],
                ["Tags", expense => expense.tags.length ? expense.tags.join(", ") : "None"],
                ["Sensitive", expense => expense.sensitive ? "Yes" : "No"],
                ["Rule bucket", expense => this.bucketLabel(expense.ruleBucket, expense.budgetBucketId)],
                ["Bucket override", expense => this.bucketLabel(expense.bucketOverride, expense.budgetBucketOverrideId)],
                ["Effective bucket", expense => this.bucketLabel(expense.bucketOverride || expense.ruleBucket, expense.budgetBucketOverrideId ?? expense.budgetBucketId)],
                ["Split", expense => expense.splitId ? `Child of split #${expense.splitId}` : "None"]
            ];
            return fields.map(([label, value]) => ({
                label,
                before: change.before ? value(change.before) : "",
                after: change.after ? value(change.after) : ""
            }));
        },
        bucketLabel(name: string | undefined, id: number | undefined): string {
            return id == null ? "None" : `${name || "Unknown bucket"} (#${id})`;
        },
        expenseDescription(update: PendingExpenseUpdate): string {
            return update.expense?.description || update.expense?.Description || `Expense #${update.id || update.Id}`;
        },
        expenseMeta(update: PendingExpenseUpdate): string {
            const expense = update.expense || update.Expense || {};
            const date = expense.date || expense.Date || "no date";
            const amount = expense.amountCents ?? expense.AmountCents;
            const source = expense.source || expense.Source || "no source";
            return `${date} · ${amount == null ? "no amount" : this.money(amount)} · ${source}`;
        },
        targetBucket(update: PendingExpenseUpdate): string {
            const bucket = update.budgetBucketOverride || update.BudgetBucketOverride;
            if (bucket) {
                return bucket.name || bucket.Name || bucket.slug || bucket.Slug || "";
            }
            const setOverride = update.setBudgetBucketOverride ?? update.SetBudgetBucketOverride;
            const bucketId = update.budgetBucketOverrideId ?? update.BudgetBucketOverrideId;
            if (setOverride && bucketId == null) {
                return "clear override";
            }
            return "";
        },
        targetTags(update: PendingExpenseUpdate): string {
            const tags = update.tags || update.Tags;
            return Array.isArray(tags) ? tags.join(", ") : "";
        },
        proposedPriority(rule: PendingRuleOrderItem): number {
            return rule.proposedPriority ?? rule.ProposedPriority ?? 0;
        },
        currentPriority(rule: PendingRuleOrderItem): number {
            return rule.currentPriority ?? rule.CurrentPriority ?? 0;
        },
        ruleBucket(rule: PendingRuleOrderItem): string {
            const bucket = rule.budgetBucket || rule.BudgetBucket;
            return bucket?.name || bucket?.Name || bucket?.slug || bucket?.Slug || "Unknown bucket";
        },
        ruleDescription(rule: PendingRuleOrderItem): string {
            return rule.description || rule.Description || `Rule #${rule.id || rule.Id}`;
        },
        money(amountCents: number): string {
            return new Intl.NumberFormat("en-IE", { style: "currency", currency: "EUR" }).format(amountCents / 100);
        }
    }
});
